/**
 * useBedConfigMutation — Sprint 31
 * TanStack Query mutation per actualitzar la config de llits i mascota d'una reserva.
 * Crida PATCH /reservations/:id/bed-config i invalida la cache del calendari.
 * Patró idèntic a usePaymentMutation.ts
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/services/api';
import type { BedType } from '@/components/calendar-v2/BedIcon';

// ============================================
// TYPES
// ============================================

export interface BedConfigItem {
  type: BedType;
  qty: number;
}

export interface UpdateBedConfigPayload {
  /** null = restaurar defecte (usa defaultBedConfig de la unit) */
  bedConfig?: BedConfigItem[] | null;
  hasPet?: boolean;
}

export interface UpdateBedConfigVariables {
  reservationId: string;
  payload: UpdateBedConfigPayload;
}

// ============================================
// HOOK
// ============================================

/**
 * Mutation per actualitzar la config de llits i mascota d'una reserva.
 *
 * @example
 * const { mutate, isPending } = useBedConfigMutation();
 * // Afegir cuna:
 * mutate({ reservationId: '123', payload: { bedConfig: [{ type: 'double', qty: 1 }, { type: 'crib', qty: 1 }] } });
 * // Restaurar defecte:
 * mutate({ reservationId: '123', payload: { bedConfig: null } });
 * // Marcar mascota:
 * mutate({ reservationId: '123', payload: { hasPet: true } });
 */
export function useBedConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateBedConfigVariables>({
    mutationFn: async ({ reservationId, payload }) => {
      await api.patch(`/reservations/${reservationId}/bed-config`, payload);
    },
    onSuccess: () => {
      // Invalida totes les queries del calendari per refrescar les dades
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
    },
  });
}

// ============================================
// CONFIRMAR (runbook 10, contracte C6)
// ============================================

export interface ConfirmBedConfigResponse {
  id: string;
  bedConfig: string | null;
  bedConfigSource: 'manual' | 'auto' | null;
}

/**
 * «Confirmar» del hover: marca els llits com a manuals sense canviar-los
 * (`POST /reservations/:id/confirm-bed-config`). Si la reserva no en tenia, el backend hi copia
 * els de l'habitació. Invalida les mateixes claus que `useBedConfigMutation`.
 */
export function useConfirmBedConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation<ConfirmBedConfigResponse, Error, string>({
    mutationFn: async (reservationId) => {
      const res = await api.post(`/reservations/${reservationId}/confirm-bed-config`);
      return res.data as ConfirmBedConfigResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
    },
    onError: () => {
      toast.error("No s'han pogut confirmar els llits");
    },
  });
}
